import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { SettingsProvider } from "@/context/SettingsContext";
import { Toaster } from "@/components/ui/sonner";
import "@/App.css";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import AppLayout from "@/components/AppLayout";
import Dashboard from "@/pages/Dashboard";
import Courses from "@/pages/Courses";
import CourseDetail from "@/pages/CourseDetail";
import Lesson from "@/pages/Lesson";
import Quiz from "@/pages/Quiz";
import Certificates from "@/pages/Certificates";
import CertificateVerify from "@/pages/CertificateVerify";
import Profile from "@/pages/Profile";
import Leaderboard from "@/pages/Leaderboard";
import Live from "@/pages/Live";
import AdminLogin from "@/pages/AdminLogin";

import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminCourseList from "@/pages/admin/AdminCourseList";
import AdminCourseNew from "@/pages/admin/AdminCourseNew";
import AdminCourseEditor from "@/pages/admin/AdminCourseEditor";
import AdminAnnouncements from "@/pages/admin/AdminAnnouncements";
import AdminLive from "@/pages/admin/AdminLive";
import AdminStudents from "@/pages/admin/AdminStudents";
import AdminAssignments from "@/pages/admin/AdminAssignments";
import AdminBackup from "@/pages/admin/AdminBackup";
import AdminSettings from "@/pages/admin/AdminSettings";

function Protected({ children, admin = false }) {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-5 bg-background">
        <img src="/brand/logo.png" alt="InfiniteSkills" className="h-20 w-20 object-contain animate-pulse" />
        <div className="h-8 w-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
        <p className="text-xs uppercase tracking-[0.25em] text-muted-foreground">InfiniteSkills Learning Portal</p>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (admin && user.role !== "admin") return <Navigate to="/app" replace />;
  return <AppLayout>{children}</AppLayout>;
}

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <SettingsProvider>
          <BrowserRouter>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/verify/:certId" element={<CertificateVerify />} />
            <Route path="/admin-login" element={<AdminLogin />} />
            <Route path="/admin" element={<AdminLogin />} />

            <Route path="/app" element={<Protected><Dashboard /></Protected>} />
            <Route path="/app/courses" element={<Protected><Courses /></Protected>} />
            <Route path="/app/courses/:slug" element={<Protected><CourseDetail /></Protected>} />
            <Route path="/app/courses/:slug/lesson/:lessonId" element={<Protected><Lesson /></Protected>} />
            <Route path="/app/courses/:slug/quiz" element={<Protected><Quiz /></Protected>} />
            <Route path="/app/certificates" element={<Protected><Certificates /></Protected>} />
            <Route path="/app/profile" element={<Protected><Profile /></Protected>} />
            <Route path="/app/leaderboard" element={<Protected><Leaderboard /></Protected>} />
            <Route path="/app/live" element={<Protected><Live /></Protected>} />

            {/* Admin */}
            <Route path="/app/admin" element={<Protected admin><AdminLayout /></Protected>}>
              <Route index element={<AdminDashboard />} />
              <Route path="courses" element={<AdminCourseList />} />
              <Route path="courses/new" element={<AdminCourseNew />} />
              <Route path="courses/:id" element={<AdminCourseEditor />} />
              <Route path="announcements" element={<AdminAnnouncements />} />
              <Route path="live" element={<AdminLive />} />
              <Route path="students" element={<AdminStudents />} />
              <Route path="assignments" element={<AdminAssignments />} />
              <Route path="backup" element={<AdminBackup />} />
              <Route path="settings" element={<AdminSettings />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </BrowserRouter>
        </SettingsProvider>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </ThemeProvider>
  );
}
